/**
 * The body — parts, and the capability scalars derived from them (AoD §3.1,
 * §3.2, §3.3).
 *
 * The one rule that makes this milestone worth building: **capabilities are the
 * only interface between injury and gameplay**. Combat asks for `mobility`,
 * medicine asks for `fineWork`, the client asks for `consciousness`. Nothing
 * outside this module and `wound.ts` reads a body part directly. Get that seam
 * right and every later system — crafting, carrying, climbing, surgery — plugs
 * into the wound model for free, and a wolf's ruined foreleg degrades its
 * gameplay through the same code path as a farmer's broken wrist.
 *
 * Body plans are **data** (`@annwn/content`), not code. This module owns the
 * schema, the validator, and the derivation; it owns no anatomy. A plan that is
 * malformed fails at load time with a message naming the part — never silently
 * at hit time, three hours into a headless balance run.
 */
/**
 * The nine hit zones. Wound tables are authored per *zone*, not per part, so
 * left and right share their entries (AoD open question 4): 9 zones instead of
 * 13 parts cuts the authoring surface by a third and loses nothing — a gash
 * across the left forearm reads the same as one across the right.
 */
export const BODY_ZONES = [
    'head',
    'eye',
    'neck',
    'chest',
    'abdomen',
    'arm',
    'hand',
    'leg',
    'foot',
];
export function isBodyZone(value) {
    return typeof value === 'string' && BODY_ZONES.includes(value);
}
/** Paired parts carry a side; the head does not. */
export const BODY_SIDES = ['left', 'right'];
/**
 * The derived scalars, all in [0, 1]. Anything that wants to know how injured
 * somebody is asks one of these seven questions, and no other.
 *
 * - `consciousness` — head and neck. At 0 the character is down. M1-04 layers
 *   blood loss and pain on top of the structural value computed here.
 * - `sight` — eyes, half each.
 * - `gripL` / `gripR` — arm and hand of that side. Weapons, ropes, tools.
 * - `fineWork` — the best usable hand. Stitching, carving, surgery.
 * - `mobility` — legs and feet, both sides pooled. Footwork and travel speed.
 * - `breath` — neck, chest, abdomen. Stamina and exertion.
 */
export const CAPABILITIES = [
    'consciousness',
    'sight',
    'gripL',
    'gripR',
    'fineWork',
    'mobility',
    'breath',
];
/**
 * Capabilities a plan may hand out directly by declaring part contributions.
 *
 * `fineWork` is absent on purpose: it is derived from the grips rather than
 * summed from parts (see `deriveCapabilities`), because "the best hand you can
 * still hold a needle with" is a rule about hands, not a share of a total. A
 * plan that tried to declare it would be declaring the answer.
 */
export const CONTRIBUTED_CAPABILITIES = CAPABILITIES.filter((capability) => capability !== 'fineWork');
/** A hand must be at least this usable before it can do fine work (AoD §3.2). */
export const FINE_WORK_GRIP_FLOOR = 0.5;
/** A broken but attached limb still does a little work. Tunable. */
export const BROKEN_FACTOR = 0.25;
export function createBodyPartState() {
    return { broken: false, severed: false, destroyed: false, blind: false, impairment: 0 };
}
export function createBody(plan) {
    const parts = {};
    for (const part of plan.parts)
        parts[part.id] = createBodyPartState();
    return { planId: plan.id, parts };
}
export function cloneBody(body) {
    const parts = {};
    for (const id of Object.keys(body.parts))
        parts[id] = { ...body.parts[id] };
    return { planId: body.planId, parts };
}
/** Thrown by `validateBodyPlan`. Loud, and it names the part. */
export class BodyPlanError extends Error {
    constructor(planId, message) {
        super(`body plan '${planId}': ${message}`);
        this.name = 'BodyPlanError';
    }
}
/** Floating-point slack for the "contributions sum to 1" check. */
const SUM_TOLERANCE = 1e-9;
/**
 * Validate a plan at load time. Everything here is a mistake that would
 * otherwise surface as a wrong number in a duel a thousand ticks later:
 * a zone with no parts (an unreachable hit-location roll), a capability whose
 * shares sum to 0.9 (a healthy character permanently at 90% grip), a duplicate
 * id (one part silently shadowing another in the body's part table).
 */
export function validateBodyPlan(plan) {
    const fail = (message) => {
        throw new BodyPlanError(plan.id, message);
    };
    if (!plan.id)
        fail('needs a non-empty id');
    if (plan.parts.length === 0)
        fail('has no parts');
    const seen = new Set();
    const totals = new Map();
    for (const part of plan.parts) {
        const where = `part '${part.id}'`;
        if (!part.id)
            fail('has a part with no id');
        if (seen.has(part.id))
            fail(`declares ${where} twice`);
        seen.add(part.id);
        if (!isBodyZone(part.zone))
            fail(`${where} has unknown zone '${part.zone}'`);
        if (!part.name)
            fail(`${where} has no display name`);
        if (part.side !== null && !BODY_SIDES.includes(part.side)) {
            fail(`${where} has unknown side '${part.side}'`);
        }
        if (!Number.isFinite(part.hitWeight) || part.hitWeight <= 0) {
            fail(`${where} needs a positive hitWeight (got ${part.hitWeight})`);
        }
        for (const key of Object.keys(part.contributes)) {
            const capability = key;
            if (!CONTRIBUTED_CAPABILITIES.includes(capability)) {
                fail(`${where} contributes to unknown capability '${key}'`);
            }
            const share = part.contributes[capability];
            if (!Number.isFinite(share) || share <= 0 || share > 1) {
                fail(`${where} contributes ${share} to ${capability}; shares are in (0, 1]`);
            }
            totals.set(capability, (totals.get(capability) ?? 0) + share);
        }
    }
    for (const [capability, total] of totals) {
        if (Math.abs(total - 1) > SUM_TOLERANCE) {
            fail(`${capability} contributions sum to ${total}, not 1`);
        }
    }
    // A zone no part claims is a hole in the hit-location table: the zone roll
    // can select it and the sub-roll then has nothing to pick.
    const zones = new Set(plan.parts.map((part) => part.zone));
    if (zones.size === 0)
        fail('covers no hit zones');
}
/**
 * Has this body lost a leg or a foot outright (M4-05)?
 *
 * Asked by `speedOf`, because a missing leg is not the same kind of slow as a
 * hurt one. Impairment scales: a cut thigh walks at some fraction and the
 * fraction is the mechanic. A leg that is *gone* does not scale — the man is on
 * the ground, and what gets him upright again is a crutch, not healing. So this
 * is a boolean rather than another term in the capability sum, and the rule it
 * gates lives in `movement.ts` where speed is decided.
 *
 * `destroyed` counts with `severed`: a foot burnt to nothing is a foot you are
 * not walking on, whatever is still attached.
 */
export function lostLeg(plan, body) {
    for (const part of plan.parts) {
        if (part.zone !== 'leg' && part.zone !== 'foot')
            continue;
        const state = body.parts[part.id];
        if (state && (state.severed || state.destroyed))
            return true;
    }
    return false;
}
/**
 * How much of its job a part can still do, in [0, 1].
 *
 * Terminal states short-circuit: a severed hand grips nothing, and no amount
 * of healing on the *stump* changes that. Otherwise a broken part works at
 * `BROKEN_FACTOR`, and soft impairment scales what is left.
 */
export function partFactor(state) {
    if (state.severed || state.destroyed || state.blind)
        return 0;
    const structural = state.broken ? BROKEN_FACTOR : 1;
    return clamp01(structural * (1 - state.impairment));
}
/**
 * The derivation (AoD §3.2). Contributed capabilities are the weighted sum of
 * their parts' factors; `fineWork` is the best hand that still clears
 * `FINE_WORK_GRIP_FLOOR`.
 *
 * A capability no part contributes to is **1**, not 0: a creature with no eyes
 * declared in its plan is not blind, it is a creature whose plan does not model
 * sight, and callers gating on `sight > 0.3` should not be told a wolf cannot
 * see. Absence of the mechanic is not the failure of it.
 *
 * Cheap enough to call per exchange, but the result is a plain object — cache
 * it against a body's revision if a caller ever needs it per tick.
 */
export function deriveCapabilities(plan, body) {
    const sums = new Map();
    const declared = new Set();
    for (const part of plan.parts) {
        const state = body.parts[part.id];
        // A body missing a part its plan declares is a bug upstream, but treating
        // it as absent rather than throwing keeps a save-migration mistake from
        // taking down a whole battle.
        const factor = state ? partFactor(state) : 0;
        for (const key of Object.keys(part.contributes)) {
            const capability = key;
            const share = part.contributes[capability];
            declared.add(capability);
            sums.set(capability, (sums.get(capability) ?? 0) + share * factor);
        }
    }
    const value = (capability) => declared.has(capability) ? round(clamp01(sums.get(capability) ?? 0)) : 1;
    const gripL = value('gripL');
    const gripR = value('gripR');
    return {
        consciousness: value('consciousness'),
        sight: value('sight'),
        gripL,
        gripR,
        mobility: value('mobility'),
        breath: value('breath'),
        fineWork: fineWorkFrom(gripL, gripR),
    };
}
/**
 * Capabilities are rounded to six places.
 *
 * Four wolf legs at 0.15 and four paws at 0.1 sum to 0.9999999999999999 in
 * IEEE-754, and an uninjured wolf that is 99.99999999999999% mobile is a
 * number no gameplay rule and no test should have to reason about. Rounding is
 * exact and platform-independent, so nothing about determinism is at risk.
 */
const CAPABILITY_PLACES = 1e6;
function round(value) {
    return Math.round(value * CAPABILITY_PLACES) / CAPABILITY_PLACES;
}
function fineWorkFrom(gripL, gripR) {
    const best = Math.max(gripL, gripR);
    return best >= FINE_WORK_GRIP_FLOOR ? best : 0;
}
export function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    if (value < 0)
        return 0;
    if (value > 1)
        return 1;
    return value;
}
export const ATTRIBUTE_RANGE = { min: 1, max: 20, average: 10 };
export function createAttributes(overrides = {}) {
    const { average } = ATTRIBUTE_RANGE;
    return {
        frame: average,
        agility: average,
        toughness: average,
        wit: average,
        presence: average,
        ...overrides,
    };
}
/**
 * Strength rating, −2…+3 — the `ST±n` term muscle-powered weapons scale by
 * (AoD §9.1). A spear is `ST+1`: an average-framed spearman does 1 more than a
 * weak one, and no amount of frame turns a spear into a siege engine.
 */
export function strengthRating(attributes) {
    return clampInt(Math.round(attributes.frame / 5) - 1, -2, 3);
}
/** Toughness rating (TO), 1–5. Subtracted from every wound level (AoD §4.3). */
export function toughnessRating(attributes) {
    return clampInt(Math.round(attributes.toughness / 4), 1, 5);
}
/**
 * Grit — how much pain a character fights through (AoD §5.2). Morale coupling
 * is deliberately deferred: AoD tied this to crew morale, and Annwn has no
 * equivalent standing to read from until M2's band system exists.
 */
export function grit(attributes) {
    return Math.floor(attributes.toughness / 5) + 1;
}
/**
 * How much of their fighting ability an injured character still brings.
 *
 * Consciousness gates everything — a character on the edge of blacking out
 * fights at a fraction regardless of their limbs — and the remainder splits
 * between the hand that holds the weapon and the feet that carry it. Healthy
 * is exactly 1.0, so an uninjured character's combat pool is the plain formula.
 */
export function capabilityFactor(capabilities) {
    const bestGrip = Math.max(capabilities.gripL, capabilities.gripR);
    return clamp01(capabilities.consciousness * (0.4 + 0.3 * bestGrip + 0.3 * capabilities.mobility));
}
/**
 * Combat Pool — the dice a fighter splits between attack and defence (AoD
 * §3.3/§7.3). Injury enters combat here and nowhere else: a wounded fighter has
 * fewer dice, which is why they hit less often *and* defend worse, which is why
 * wounds compound the way they should.
 */
export function combatPool(attributes, capabilities) {
    const base = Math.round((attributes.frame + attributes.agility) / 2);
    return Math.max(0, Math.round(base * capabilityFactor(capabilities)));
}
function clampInt(value, min, max) {
    return Math.max(min, Math.min(max, Math.round(value)));
}
//# sourceMappingURL=body.js.map